// FLAG:useDocuAi — name the e-invoice format a document actually is, so the review's validity
// state is attributed instead of being an unexplained green tick. Fed by serialize.js `eInvoice`
// (DocuAI contract: KoSIT profile + invoiceFormat + detectedSyntax), which is only ever attached
// for useDocuAi tenants — every consumer therefore renders exactly as before without it.

import lang from '@/lang'

export interface EInvoiceInfo {
  valid?: boolean | null
  profile?: string | null
  format?: string | null
  syntax?: string | null
  errorCount?: number | null
  warningCount?: number | null
}

// The KoSIT scenario is the most precise thing we have (it IS the ruleset the document was
// validated against), so it wins over the contract's format id. Matched by PREFIX: the scenario
// names carry a version digit ('zugferd1', 'zugferd2', 'xrechnung3'), which is validator-internal
// and would only confuse the reviewer.
const PROFILE_PREFIX_LABELS: Array<[string, string]> = [
  ['zugferd', 'ZUGFeRD'],
  ['factur', 'Factur-X'],
  ['xrechnung', 'XRechnung'],
]

const SYNTAX_LABELS: Record<string, string> = {
  cii: 'CII',
  ubl: 'UBL',
}

const normalize = (value?: string | null) => (value || '').trim().toLowerCase()

/**
 * A human label for the e-invoice format ('ZUGFeRD', 'XRechnung', 'E-Rechnung (CII)'), or null
 * when the document is not a structured e-invoice.
 */
export const getEInvoiceFormatLabel = (eInvoice?: EInvoiceInfo | null): string | null => {
  if (!eInvoice) { return null }

  const profile = normalize(eInvoice.profile)
  const matched = profile && PROFILE_PREFIX_LABELS.find(([prefix]) => profile.startsWith(prefix))
  if (matched) { return matched[1] }

  // 'en16931-cii' / 'en16931-ubl' — the guideline is not a product name the user knows, so show
  // the syntax it resolves to rather than the raw id.
  const format = normalize(eInvoice.format)
  const formatSyntax = format.endsWith('-cii') ? 'CII' : (format.endsWith('-ubl') ? 'UBL' : null)
  if (formatSyntax) { return lang.documents.eInvoiceFormat(formatSyntax) }

  const syntax = SYNTAX_LABELS[normalize(eInvoice.syntax)]
  if (syntax) { return lang.documents.eInvoiceFormat(syntax) }

  return format ? lang.documents.eInvoiceFormat(eInvoice.format) : null
}
